import { AnimationLayer } from "@manager/layer/AnimationLayer";
import { AnimationLayerObserver } from "@manager/layerObserver/AnimationLayerObserver";
import React from "react";
import { ListenerHelper } from "../../utils/ListenerHelper";
import { WindowResizeHelper } from "../../utils/WindowResizeHelper";

import "@styles/c-animationLayer.scss";

// todo static typing
declare let lottie: any;
type LottieAnimation = any;

interface Props {
  className?: string;
}

export class AnimationLayerComponent extends React.Component<Props> implements AnimationLayer {
  private static DATA_SRC_ATTRIBUTE = "data-src";

  private observer: AnimationLayerObserver;

  private lottieContainer: HTMLElement;
  private animations: { [src: string]: LottieAnimation } = {};

  constructor(props: Props) {
    super(props);
  }

  render() {
    return <div className={`${this.props.className || ""} c-animationLayer`} ref={(element) => (this.lottieContainer = element)} />;
  }

  componentDidMount() {
    ListenerHelper.addEventListener("window", "resize", this.handleWindowResize);
  }

  componentWillUnmount() {
    ListenerHelper.removeEventListener("window", "resize", this.handleWindowResize);
  }

  // start of AnimationLayer implementation
  clearAllAnimations(): void {
    for (const src of Object.keys(this.animations)) {
      this.removeAnimation(src);
    }
  }

  loadAnimation(src: string, animationJSON: any, animationBlur: string): void {
    const animationContainer = this.createAndAppendAnimationContainer(src, animationBlur);
    const animation = lottie.loadAnimation({
      container: animationContainer,
      renderer: "svg",
      loop: false,
      autoplay: false,
      animationData: animationJSON,
    });

    this.animations[src] = animation;

    if (this.observer) {
      animation.onComplete = () => {
        this.observer.onEnded();
      };
    }
  }

  removeAnimation(src: string): void {
    const animation = this.animations[src];
    delete this.animations[src];

    animation && animation.destroy();

    const animationContainer = this.lottieContainer.querySelector(`[${AnimationLayerComponent.DATA_SRC_ATTRIBUTE}="${src}"]`);
    animationContainer && animationContainer.parentNode.removeChild(animationContainer);
  }

  getAnimationCurrentTime(src: string): number {
    const animation = this.animations[src];
    return animation && animation.currentFrame / animation.frameRate;
  }

  getAnimationDuration(src: string): number {
    const animation = this.animations[src];
    return animation && animation.totalFrames / animation.frameRate;
  }

  getIsAnimationPlaying(src: string): boolean {
    const animation = this.animations[src];
    return animation && !animation.isPaused;
  }

  playAnimation(src: string): void {
    const animation = this.animations[src];
    animation && animation.play();
  }

  pauseAnimation(src: string): void {
    const animation = this.animations[src];
    animation && animation.pause();
  }

  moveTo(animationSrc: string, timeMillis: number, play: boolean = false): void {
    const animationItem = this.animations[animationSrc];
    if (play) {
      animationItem.goToAndPlay(timeMillis, false);
    } else {
      animationItem.goToAndStop(timeMillis, false);
    }
  }

  registerObserver(observer: AnimationLayerObserver): void {
    this.observer = observer;
  }

  // end of AnimationLayer implementation

  private createAndAppendAnimationContainer(animationSrc: string, animationBlur: string): HTMLElement {
    const container = document.createElement("div");
    container.setAttribute(AnimationLayerComponent.DATA_SRC_ATTRIBUTE, animationSrc);
    container.setAttribute("class", "c-animationLayer__animationContainer");
    container.style.setProperty("--animation-blur", animationBlur);

    const insideIframe = window.self !== window.top;
    if (insideIframe) {
      // without position: fixed animations in Safari on iphone in iframe are not scaled correctly
      container.style.position = "fixed";
      if (/Edge/.test(navigator.userAgent)) {
        // without position: absolute animations in Edge are not scaled correctly
        container.style.position = "absolute";
      }
    }

    this.lottieContainer.appendChild(container);
    return container;
  }

  private handleWindowResize = () => WindowResizeHelper.doOnResize(() => this.resizeAllAnimations());

  private resizeAllAnimations() {
    for (const src of Object.keys(this.animations)) {
      this.animations[src].resize();
    }
  }
}
